// Library — the public shelf.
//
// Two sections:
//   Published — every book flagged visibility='published' (public endpoint,
//               works without auth so logged-out visitors see the shelf).
//   Yours     — the logged-in user's own books of any visibility. Only
//               rendered when a token is present.

import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { api, ApiError } from "../lib/api";
import { getToken } from "../lib/auth";
import { prefetchBook } from "../lib/prefetch";
import { Shell } from "../components/Shell";
import { Constellation } from "../components/Constellation";

interface OwnedBook {
  id: string;
  title: string;
  slug: string;
  shape: string;
  status: string;
  created_at: string;
}

interface PublishedBook {
  id: string;
  title: string;
  slug: string;
  shape: string;
  target_length: string;
  template_preset: string;
  status: string;
  created_at: string;
  author_name: string | null;
  cover_url: string | null;
  blurb: string | null;          // short vision teaser surfaced on the card
  // Per-book library overrides (backend alembic 0033). null → fall back
  // to the shape-based shelf / newest-first order.
  library_category: string | null;
  library_rank: number | null;
}

export default function Library() {
  const authed = !!getToken();

  const [published, setPublished] = useState<PublishedBook[] | null>(null);
  const [publishedError, setPublishedError] = useState<string | null>(null);

  // "Yours" strip removed — no longer fetching the owner's books on the
  // public library page. Author-specific surface lives at /my-books.

  // Raw keystroke state + debounced query. 150 ms is the sweet spot —
  // long enough to avoid thrash while typing, short enough to feel live.
  const [rawQuery, setRawQuery] = useState("");
  const [query, setQuery] = useState("");
  useEffect(() => {
    const id = setTimeout(() => setQuery(rawQuery.trim().toLowerCase()), 150);
    return () => clearTimeout(id);
  }, [rawQuery]);

  // null = "All shelves" (default grouped view). One category = single shelf.
  const [activeCategory, setActiveCategory] = useState<Category | null>(null);

  useEffect(() => {
    let cancel = false;
    api.get<PublishedBook[]>("/api/library", { auth: false })
      .then((d) => { if (!cancel) setPublished(d); })
      .catch((e) => { if (!cancel) setPublishedError(e instanceof ApiError ? e.detail : "Could not load the shelf."); });

    return () => { cancel = true; };
  }, [authed]);

  // Chip counts are over the unfiltered set so chips don't collapse to
  // zero while the search narrows — they still report the shelf's size.
  const counts = useMemo(() => {
    const c: Record<Category, number> = { "Fiction": 0, "Non Fiction": 0, "AI & Compute": 0, "Deep Tech": 0, "Picture Books": 0, "Science & Math": 0, "Indian Wisdom": 0, "Business": 0, "Finance": 0, "Self-Help": 0, "Psychology": 0, "Philosophy": 0, "Exam Prep": 0, "Other": 0 };
    for (const b of published ?? []) c[categoryForBook(b)]++;
    return c;
  }, [published]);

  const clearFilters = () => { setRawQuery(""); setActiveCategory(null); };

  return (
    <Shell>
      <section className="px-5 sm:px-8 md:px-12 pt-12 sm:pt-16 pb-6">
        <div className="max-w-[1380px] mx-auto">
          <div className="flex items-end justify-between gap-8 flex-wrap">
            <div>
              <div className="eyebrow mb-3">The library</div>
              <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-normal text-vellum-50 leading-tight">
                Books dreamed into being.
              </h1>
              <p className="mt-3 sm:mt-4 font-body italic text-ink-100 text-sm sm:text-base max-w-2xl">
                A shelf that grows with every book an author is willing to share —
                yours and others'. Start a new one from the top, or open one below.
              </p>
            </div>
            <SearchInput value={rawQuery} onChange={setRawQuery} total={published?.length ?? 0} />
          </div>

          <CategoryChips
            active={activeCategory}
            onChange={setActiveCategory}
            counts={counts}
            total={published?.length ?? 0}
          />
        </div>
      </section>

      {/* "Yours" strip removed — author-specific surface lives at /my-books;
          /library is now purely the public shelf for everyone. */}

      <PublishedShelves
        books={published}
        error={publishedError}
        query={query}
        activeCategory={activeCategory}
        onClearFilters={clearFilters}
      />
    </Shell>
  );
}


// =============================================================
// Search + filter UI
// =============================================================

function SearchInput({ value, onChange, total }: { value: string; onChange: (s: string) => void; total: number }) {
  return (
    <div className="relative w-full sm:w-[360px] shrink-0">
      <svg viewBox="0 0 20 20" className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-500" aria-hidden>
        <circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" strokeWidth="1.6"/>
        <line x1="13.5" y1="13.5" x2="17" y2="17" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round"/>
      </svg>
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={total ? `Search ${total} books…` : "Search the library…"}
        className="w-full pl-10 pr-10 py-2.5 bg-ink-800/50 border border-ink-700 rounded-sm font-body text-sm text-vellum-50 placeholder:text-ink-500 focus:border-gold-500/60 focus:outline-none transition-colors"
        aria-label="Search the library"
      />
      {value ? (
        <button
          type="button"
          onClick={() => onChange("")}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-ink-500 hover:text-ink-100 font-mono text-xs"
          aria-label="Clear search"
        >
          ✕
        </button>
      ) : null}
    </div>
  );
}

function CategoryChips({
  active, onChange, counts, total,
}: {
  active: Category | null;
  onChange: (c: Category | null) => void;
  counts: Record<Category, number>;
  total: number;
}) {
  const chip = (label: string, value: Category | null, count: number) => {
    const isActive = active === value;
    const disabled = count === 0 && value !== null;
    return (
      <button
        key={label}
        type="button"
        disabled={disabled}
        onClick={() => onChange(value)}
        className={`font-mono text-[10px] tracking-smallcaps uppercase px-3 py-1.5 rounded-sm border transition-colors
          ${isActive
            ? "bg-gold-500 text-ink-900 border-gold-500"
            : disabled
              ? "border-ink-800 text-ink-700 cursor-not-allowed"
              : "border-ink-700 text-ink-300 hover:border-gold-500/60 hover:text-vellum-50"}`}
      >
        {label} <span className={`ml-1 ${isActive ? "text-ink-900/70" : "text-ink-500"}`}>{count}</span>
      </button>
    );
  };

  return (
    <div className="mt-8 flex flex-wrap gap-2 border-t border-ink-700 pt-5">
      {chip("All shelves", null, total)}
      {CATEGORY_ORDER.map((cat) => chip(cat, cat, counts[cat]))}
    </div>
  );
}


// =============================================================
// PublishedShelves — grouped by category (Science / Philosophy / Fiction / Other).
//
// Mapping is driven off book.shape — the same value the engine uses for
// dispatch. See ROADMAP.md § "deconstructed storage" for the longer story
// on richer taxonomy. For now: three shelves + "Other" for anything unknown.
// =============================================================

type Category =
  | "Fiction"
  | "Non Fiction"
  | "AI & Compute"
  | "Deep Tech"
  | "Picture Books"
  | "Science & Math"
  | "Indian Wisdom"
  | "Business"
  | "Finance"
  | "Self-Help"
  | "Psychology"
  | "Philosophy"
  | "Exam Prep"
  | "Other";

// Display order for the shelves. Empty categories are hidden at render time.
// Category presentation order — narrative-first.
//   Fiction       → most accessible, on top
//   Picture Books → split out 2026-05-09 so children's titles sit on their
//                   own shelf rather than getting buried in Fiction
//   Nonfiction    → essays, memoir, practical (split out from Philosophy 2026-04-26)
//   Philosophy    → tradition: commentaries, readers, anthologies
//   AI & Compute  → atlas-technical
//   Science       → physics, math, engineering — last because they're heaviest
const CATEGORY_ORDER: Category[] = [
  "Fiction", "Non Fiction",
  // v3.44 — non-fiction split (Mukesh 2026-06-10): Business narrowed,
  // Finance/Self-Help/Psychology get their own shelves. Empty shelves
  // stay hidden, so these only render once books are shelved onto them.
  "Business", "Finance", "Self-Help", "Psychology",
  "AI & Compute", "Deep Tech",
  "Picture Books", "Science & Math", "Indian Wisdom",
  "Philosophy", "Exam Prep", "Other",
];

const CATEGORY_COPY: Record<Category, string> = {
  "Fiction":       "Novels and stories.",
  "Non Fiction":   "Essays, memoir, history — argument and witness.",
  "AI & Compute":  "LLMs, agents, alignment, the systems that run them.",
  "Deep Tech":     "Robotics, biotech, energy, quantum — the next stacks.",
  "Picture Books": "Illustrated read-aloud and middle-grade.",
  "Science & Math":"Physics, mathematics, engineering — atlases.",
  "Indian Wisdom": "Vedas, Upanishads, Gita — the Indian textual tradition.",
  "Business":      "Startups, strategy, operating — how companies get built.",
  "Finance":       "Money, markets, investing — how capital works.",
  "Self-Help":     "Habits, craft, practice — books that change what you do.",
  "Psychology":    "Minds, biases, behavior — why people do what they do.",
  "Philosophy":    "Eastern + Western tradition.",
  "Exam Prep":     "JEE, CBSE, NEET — reference and practice.",
  "Other":         "Books that don't fit the shelves above.",
};

function categoryFor(shape: string): Category {
  // 9-shelf scheme (Mukesh 2026-06-04). library_category override wins;
  // this maps engine shape → default shelf when no override is set.
  if (shape === "exam-prep") return "Exam Prep";
  if (shape === "picture-book" || shape === "graphic-novel") return "Picture Books";
  if (shape.startsWith("novel") || shape === "stories"
      || shape === "ya" || shape === "poetry") return "Fiction";
  if (shape.startsWith("atlas-technical")) return "AI & Compute";
  if (shape.startsWith("atlas-physics")) return "Science & Math";
  if (shape === "atlas" || shape === "monograph") return "Science & Math";
  if (shape.startsWith("atlas-humanities")) return "Philosophy";
  if (shape === "commentary" || shape === "anthology" || shape === "reader") return "Philosophy";
  return "Non Fiction";
}

// Per-book shelf resolution. A book with a valid `library_category`
// override (backend alembic 0033) lands on that shelf regardless of its
// shape; otherwise it falls back to the shape map. Lets one book move
// shelves without dragging its shape-siblings (e.g. Cities Before Empire
// is atlas-humanities → Philosophy by shape, but belongs in Nonfiction).
// Derived from CATEGORY_ORDER so a new shelf only ever needs to be added
// in ONE place. (v3.45 — Finance/Self-Help/Psychology silently fell back to
// the shape map because this used to be a fourth hand-maintained list.)
const VALID_CATEGORIES = new Set<Category>(CATEGORY_ORDER);
function categoryForBook(b: PublishedBook): Category {
  if (b.library_category && VALID_CATEGORIES.has(b.library_category as Category)) {
    return b.library_category as Category;
  }
  return categoryFor(b.shape);
}

function matchesQuery(b: PublishedBook, q: string): boolean {
  if (!q) return true;
  const haystack = `${b.title} ${b.author_name ?? ""} ${b.shape}`.toLowerCase();
  // Split query on whitespace — each term must match somewhere in the
  // haystack. Lets users type "sufi poetry" and find a Sufi-poetry book
  // regardless of word order.
  return q.split(/\s+/).every((t) => haystack.includes(t));
}

function PublishedShelves({
  books, error, query, activeCategory, onClearFilters,
}: {
  books: PublishedBook[] | null;
  error: string | null;
  query: string;
  activeCategory: Category | null;
  onClearFilters: () => void;
}) {
  if (error) {
    return (
      <section className="px-5 sm:px-8 md:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1380px] mx-auto rounded-sm border border-oxblood/40 bg-ink-800 p-6 text-sm text-oxblood">
          {error}
        </div>
      </section>
    );
  }
  if (books === null) {
    return (
      <section className="px-5 sm:px-8 md:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1380px] mx-auto grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-5" aria-hidden>
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="rounded-sm border border-ink-700 overflow-hidden">
              <div className="skeleton" style={{ aspectRatio: "2 / 3" }} />
              <div className="p-3">
                <div className="skeleton h-2.5 w-1/3 mb-2" />
                <div className="skeleton h-3 w-4/5" />
              </div>
            </div>
          ))}
        </div>
      </section>
    );
  }
  if (books.length === 0) {
    return (
      <section className="px-5 sm:px-8 md:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1380px] mx-auto">
          <div className="border-t border-ink-700 pt-10">
            <EmptyShelf kind="public" />
          </div>
        </div>
      </section>
    );
  }

  const isFiltered = query !== "" || activeCategory !== null;

  // Flat-search mode: query is active. Show one pooled result list across
  // all categories — search should break the shelf grouping.
  if (query !== "") {
    const results = books.filter((b) => matchesQuery(b, query))
                         .filter((b) => activeCategory === null || categoryForBook(b) === activeCategory);
    return (
      <section className="px-5 sm:px-8 md:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1380px] mx-auto">
          <div className="border-t border-ink-700 pt-10">
            <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
              <div>
                <div className="eyebrow mb-1">Search</div>
                <h2 className="font-serif text-2xl text-vellum-50">
                  {results.length} {results.length === 1 ? "match" : "matches"} for &ldquo;{query}&rdquo;
                  {activeCategory ? <span className="text-ink-300"> in {activeCategory}</span> : null}
                </h2>
              </div>
              <button onClick={onClearFilters} className="font-mono text-[10px] tracking-smallcaps uppercase text-ink-300 hover:text-vellum-50">
                Clear ✕
              </button>
            </div>
            {results.length > 0
              ? <BookGrid books={results} kind="published" />
              : <NoResults onClear={onClearFilters} />
            }
          </div>
        </div>
      </section>
    );
  }

  // Single-shelf mode: a category is picked but no query.
  if (activeCategory !== null) {
    const list = books.filter((b) => categoryForBook(b) === activeCategory);
    return (
      <section className="px-5 sm:px-8 md:px-12 pb-12 sm:pb-16">
        <div className="max-w-[1380px] mx-auto">
          <div className="border-t border-ink-700 pt-10">
            <div className="flex items-baseline justify-between mb-1 gap-4 flex-wrap">
              <div className="eyebrow">{activeCategory}</div>
              {isFiltered ? (
                <button onClick={onClearFilters} className="font-mono text-[10px] tracking-smallcaps uppercase text-ink-300 hover:text-vellum-50">
                  Show all shelves ✕
                </button>
              ) : null}
            </div>
            <h2 className="font-serif text-2xl text-vellum-50 mb-1">
              {list.length} {list.length === 1 ? "book" : "books"}
            </h2>
            <p className="font-body italic text-sm text-ink-300 mb-6">{CATEGORY_COPY[activeCategory]}</p>
            {list.length > 0
              ? <BookGrid books={list} kind="published" />
              : <NoResults onClear={onClearFilters} />
            }
          </div>
        </div>
      </section>
    );
  }

  // Default: all shelves grouped in display order.
  const groups: Record<Category, PublishedBook[]> = {
    "Fiction": [], "Non Fiction": [], "AI & Compute": [], "Deep Tech": [],
    "Picture Books": [], "Science & Math": [], "Indian Wisdom": [],
    "Business": [], "Finance": [], "Self-Help": [], "Psychology": [], "Philosophy": [], "Exam Prep": [], "Other": [],
  };
  // books arrive library_rank-ordered (ranked first, then newest) from
  // the API, so pushing in iteration order preserves the curated rank
  // within each shelf.
  for (const b of books) groups[categoryForBook(b)].push(b);

  return (
    <>
      {CATEGORY_ORDER.map((cat) => {
        const list = groups[cat];
        if (list.length === 0) return null;
        return (
          <section key={cat} className="px-5 sm:px-8 md:px-12 pb-10 sm:pb-14">
            <div className="max-w-[1380px] mx-auto">
              <div className="border-t border-ink-700 pt-10">
                <div className="eyebrow mb-2">{cat}</div>
                <h2 className="font-serif text-2xl text-vellum-50 mb-1">
                  {list.length} {list.length === 1 ? "book" : "books"}
                </h2>
                <p className="font-body italic text-sm text-ink-300 mb-6">{CATEGORY_COPY[cat]}</p>
                <BookGrid books={list} kind="published" />
              </div>
            </div>
          </section>
        );
      })}
    </>
  );
}

function NoResults({ onClear }: { onClear: () => void }) {
  return (
    <div className="rounded-sm border border-ink-700 bg-ink-800/40 px-8 py-10 text-center">
      <p className="font-serif italic text-ink-100 text-lg mb-3">
        No books match this search.
      </p>
      <p className="font-body text-ink-300 text-sm mb-6">
        Try a different title, author, or shelf.
      </p>
      <button onClick={onClear} className="font-mono text-[11px] tracking-smallcaps uppercase text-gold-500 hover:text-gold-300 border border-gold-500/40 hover:border-gold-500 rounded-sm px-3 py-1.5 transition-colors">
        Clear filters
      </button>
    </div>
  );
}




// =============================================================
// Shared types + EmptyShelf
// =============================================================

type AnyBook = OwnedBook | PublishedBook;

function EmptyShelf({ kind }: { kind: "owner" | "public" }) {
  if (kind === "public") {
    return (
      <div className="rounded-sm border border-ink-700 bg-ink-800/40 px-10 py-12 text-center">
        <p className="font-body italic text-ink-300">
          The shelf is empty — be the first to publish a book.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-sm border border-ink-700 bg-ink-800/40 px-10 py-12 text-center">
      <h3 className="font-serif text-2xl text-vellum-50 mb-2">No books yet.</h3>
      <p className="font-body italic text-ink-300 max-w-md mx-auto mb-6">
        Every book starts as a question. When you're ready, ask yours — or talk one through with the atelier.
      </p>
      <div className="flex items-center justify-center gap-4">
        <Link to="/dreambook" className="btn-primary">Begin a book</Link>
      </div>
      <div className="mt-8 flex justify-center opacity-70">
        <Constellation />
      </div>
    </div>
  );
}


// =============================================================
// Card grid
// =============================================================

function BookGrid({ books, kind }: { books: AnyBook[]; kind: "owned" | "published" }) {
  // Public shelf — denser grid: 2 (mobile) → 3 (tablet) → 4 (laptop) →
  // 5 (desktop). Removes the right-edge whitespace on wide displays.
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
      {books.map((b) => (
        <BookCard key={b.id} book={b} kind={kind} />
      ))}
    </div>
  );
}

function BookCard({ book, kind }: { book: AnyBook; kind: "owned" | "published" }) {
  const authorName = (book as PublishedBook).author_name;
  const coverUrl = (book as PublishedBook).cover_url ?? null;
  const blurb = (book as PublishedBook).blurb ?? null;
  const isComplete = book.status === "complete" || book.status === "published";

  // Prefetch the book's detail payload (which now carries pdf_url) on
  // hover. By the time the user actually clicks the card, the metadata
  // is already in the browser's HTTP cache and the PDF bytes have started
  // streaming. Cuts perceived latency on click-to-first-page by ~1-2s.
  const prefetched = useRef(false);
  function onHoverPrefetch() {
    if (prefetched.current) return;
    prefetched.current = true;
    prefetchBook(book.id, isComplete);
  }

  return (
    <article
      className="group relative rounded-sm border border-ink-700 bg-ink-800/50 overflow-hidden transition-colors duration-200 ease-observatory hover:border-gold-500"
      onMouseEnter={onHoverPrefetch}
      onTouchStart={onHoverPrefetch}
    >
      <Link to={`/books/${book.id}`} className="block">
        {/* Cover container — fixed 2:3 ratio (the standard book-cover
            aspect). object-cover keeps every cover identically framed
            regardless of the source image's intrinsic ratio (PDF page-1
            extracts vary, gpt-image-1 returns 2:3). Aspect set inline so
            we don't depend on Tailwind compiling arbitrary-value classes
            — the deployed CSS was missing aspect-[2/3] which is why
            covers were rendering at intrinsic image size 2026-04-27. */}
        <div
          style={{ aspectRatio: "2 / 3" }}
          className="w-full bg-ink-900 flex items-center justify-center border-b border-ink-700 overflow-hidden"
        >
          {coverUrl ? (
            <img
              src={coverUrl}
              alt={book.title}
              loading="lazy"
              className="w-full h-full object-cover"
            />
          ) : (
            <CoverPlaceholder title={book.title} shape={book.shape} />
          )}
        </div>
        {/* Body — natural height (no flex-1 stretch) so each card sizes to
            its actual content; rows align by cover-bottom. Title size and
            blurb-clamp are uniform across the grid. */}
        <div className="p-3 sm:p-3.5">
          {/* Owned cards still show the build status (draft/building/complete);
              published cards used to leak the raw shape slug here ("commentary",
              "atlas-humanities", "essays") which confused readers and added no
              value above the title. Dropped 2026-04-27 — the title and blurb
              tell the reader everything they need to know, and the shelf the
              card sits on already implies the category. */}
          {kind === "owned" ? (
            <div className="flex items-center gap-2 mb-1">
              <StatusDot status={book.status} />
              <span className="font-mono text-[9px] text-ink-300 uppercase tracking-smallcaps">{book.status}</span>
            </div>
          ) : null}
          <h3 className="font-serif text-[14px] sm:text-[14.5px] text-vellum-50 group-hover:text-gold-300 transition-colors duration-200 leading-snug line-clamp-2 min-h-[2.6em]">
            {book.title}
          </h3>
          {authorName ? (
            <p className="mt-1 font-mono text-[9px] tracking-smallcaps uppercase text-ink-500 truncate">
              — {authorName}
            </p>
          ) : null}
          {/* Vision blurb — clamped to 2 lines (down from 3) so cards
              stay tighter and don't leave dead space at the bottom. */}
          {blurb ? (
            <p className="mt-1.5 font-body italic text-[12px] leading-snug text-ink-300 line-clamp-2">
              {blurb}
            </p>
          ) : null}
        </div>
      </Link>
      {isComplete ? (
        <Link
          to={`/books/${book.id}/read`}
          className="absolute top-3 right-3 font-mono text-[10px] tracking-smallcaps text-gold-500 hover:text-gold-300 bg-ink-900/80 backdrop-blur-sm border border-gold-500/40 hover:border-gold-500 rounded-sm px-2 py-1 transition-colors"
          onClick={(e) => e.stopPropagation()}
        >
          Read →
        </Link>
      ) : null}
    </article>
  );
}

function CoverPlaceholder({ title, shape }: { title: string; shape: string }) {
  // Typography placeholder for books without a generated cover. First letter
  // of the title, subtle gold rule, preset label.
  const initial = title.slice(0, 1).toUpperCase();
  return (
    <div className="flex flex-col items-center justify-center px-6 py-10 text-center w-full h-full">
      <div className="font-serif text-7xl text-gold-500/70">{initial}</div>
      <div className="h-px w-8 bg-gold-500/60 my-4" />
      <div className="font-mono text-[10px] tracking-smallcaps uppercase text-ink-500">{shape}</div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === "complete" || status === "published" ? "bg-gold-500" :
    status === "building" ? "bg-teal" :
    status === "failed"   ? "bg-oxblood" :
    status === "cancelled" ? "bg-ink-500" :
    "bg-ink-500";
  return <span className={`inline-block w-1.5 h-1.5 rounded-full ${color}`} />;
}
